import type { Reducer } from '@reduxjs/toolkit';
import { createAction } from '@reduxjs/toolkit';
import type { NavigateFunction } from 'react-router';
import type { DeepReadonly } from 'ts-essentials';

import { removeJwt } from '@jane/shared/auth';
import { isEmbeddedMode, monolithPaths as paths } from '@jane/shared/util';

import type { CustomerAction, CustomerThunkAction } from '../customerAction';
import type { AppState } from '../redux';
import { closeModal, openModal } from './application';

export const HANDLE_INVALID_LOGIN_TOKEN = 'customer/handle-invalid-login-token';
const handleInvalidLoginTokenAction = createAction<
  void,
  typeof HANDLE_INVALID_LOGIN_TOKEN
>(HANDLE_INVALID_LOGIN_TOKEN);

export const handleInvalidLoginToken =
  (redirectToLogin = false, navigate?: NavigateFunction): CustomerThunkAction =>
  (dispatch, _, extraArgs) => {
    const appMode = extraArgs?.current?.appMode;
    const isEmbed = isEmbeddedMode(appMode);
    const showLoginModal = !isEmbed && !redirectToLogin;
    removeJwt();
    if (showLoginModal) {
      dispatch(closeModal());
    }
    dispatch(handleInvalidLoginTokenAction());

    if (showLoginModal) {
      dispatch(openModal({ name: 'login' }));
    } else if (isEmbed) {
      navigate && navigate(paths.embeddedLogin());
    }
  };

const SET_LOGIN_VALUE = 'customer/set-login-value';
export const setLoginValue = createAction<
  {
    name: string;
    value: string;
  },
  typeof SET_LOGIN_VALUE
>(SET_LOGIN_VALUE);

export type LoginActions =
  | ReturnType<typeof setLoginValue>
  | ReturnType<typeof handleInvalidLoginTokenAction>;

type LoginState = DeepReadonly<{
  user: {
    email: string;
    password: string;
    phone?: string;
  };
}>;

const getInitialState = (): LoginState => ({
  user: {
    email: '',
    password: '',
    phone: '',
  },
});

export const loginReducer: Reducer<LoginState, CustomerAction> = (
  state = getInitialState(),
  action
) => {
  switch (action.type) {
    case SET_LOGIN_VALUE: {
      const { name, value } = action.payload;
      return { user: { ...state.user, [name]: value } };
    }
  }

  return state;
};

export const selectLogin = (state: AppState) => state.login;
